import { createContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { THEMES } from '../constants';

export type SizeType = 'small' | 'medium' | 'large';
export interface Settings {
  theme?: string;
  fontSize?: SizeType;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  theme: THEMES.METBUSLIGHT,
  fontSize: 'medium',
};

export const restoreSettings = (): Settings | null => {
  let settings: Settings | null = null;

  try {
    const storedData: string | null = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData) as Settings;
    } else {
      settings = {
        fontSize: 'medium',
        theme: window.matchMedia('(prefers-color-scheme: dark)').matches
          ? THEMES.METBUSDARK
          : THEMES.METBUSLIGHT,
      };
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {
    // This is an empty object.
  },
});

export const SettingsProvider = (props: SettingsProviderProps) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
